import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import UnifiedPendingQueue from "@/components/UnifiedPendingQueue";
import api from "@/lib/api";

/**
 * ParentModeration (Iteration 124) — single unified Pending Queue.
 *
 * Every pending item type (kid-submitted listings, kid-edit re-approvals,
 * pending purchase reservations, allowance asks) renders in ONE list
 * via <UnifiedPendingQueue />. LISTING cards expose the canonical
 * 4-action strip: Approve · Reject (with note) · Edit (full modal) ·
 * Delete (with confirm).
 */
export default function ParentModeration() {
  const [kids, setKids] = useState([]);

  const loadKids = useCallback(async () => {
    try {
      const { data } = await api.get("/kids");
      setKids(data || []);
    } catch { setKids([]); }
  }, []);

  useEffect(() => { loadKids(); }, [loadKids]);

  const kidsById = useMemo(
    () => Object.fromEntries((kids || []).map((k) => [k.id, k])),
    [kids],
  );

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="parent-moderation-page">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        <Link
          to="/dashboard/parent"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-brandSlate transition"
          data-testid="parent-moderation-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex items-center gap-3 mt-6 mb-2">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-brandTeal text-white">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brandTeal">Moderation Queue</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900">
              Moderation queue
            </h1>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-6 max-w-2xl">
          Listings your kids submitted, waiting for your approval.
        </p>

        <UnifiedPendingQueue kidsById={kidsById} />
      </section>
      <Footer />
    </div>
  );
}
